import { Modal, Button } from 'antd';
import { useState, useEffect } from 'react';
import { useNavigation } from '@/common/hooks/navigation';
import { history } from 'umi';
import posthog from 'posthog-js';
import './index.scss';

const PatrimoinePopup = () => {
  const navigation = useNavigation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentPath, setCurrentPath] = useState(
    window.location.pathname + window.location.search,
  );
  const [homePageStartTime, setHomePageStartTime] = useState(null);

  useEffect(() => {
    const unlisten = history.listen(({ pathname, search }) => {
      setCurrentPath(pathname + search);
    });
    return () => unlisten();
  }, []);

  const popupTimerDuration = 2 * 60 * 1000;

  useEffect(() => {
    const popupShown = localStorage.getItem('patrimoinePopupShown');
    const hasVisitedPatrimoine = localStorage.getItem('hasVisitedPatrimoine');
    const isHomePage = /^\/home(\?.*)?$/.test(currentPath);
    const currentPageParam = new URLSearchParams(window.location.search).get(
      'page',
    );
    const hasPageParam = !!currentPageParam;
    const isPage2 = currentPageParam === '2';

    // Only consider pages that are home pages WITH page parameter, but NOT page=2
    const isTargetPage = isHomePage && hasPageParam && !isPage2;

    if (popupShown) return;

    // Reset timer when NOT on target page
    if (!isTargetPage) {
      setHomePageStartTime(null);
      return;
    }

    // Start timer only when on target page and timer hasn't started yet
    if (homePageStartTime === null) {
      setHomePageStartTime(Date.now());
    }

    // Set up timer for time-based popup - only runs when on target page
    const timer = setTimeout(() => {
      // Double-check we're still on target page and timer is valid
      const currentIsTargetPage =
        /^\/home(\?.*)?$/.test(
          window.location.pathname + window.location.search,
        ) &&
        new URLSearchParams(window.location.search).get('page') &&
        new URLSearchParams(window.location.search).get('page') !== '2';

      if (
        !localStorage.getItem('patrimoinePopupShown') &&
        currentIsTargetPage &&
        homePageStartTime &&
        Date.now() - homePageStartTime >= popupTimerDuration
      ) {
        setIsVisible(true);
        if (process.env.NODE_ENV !== 'development') {
          posthog.capture('patrimoine_popup_shown', {
            trigger: 'time_based',
            path: currentPath,
          });
        }
      }
    }, popupTimerDuration);

    // Handle return visit logic - only for target pages
    if (hasVisitedPatrimoine && !isPage2) {
      // Show popup immediately for return visitors
      setIsVisible(true);
      if (process.env.NODE_ENV !== 'development') {
        posthog.capture('patrimoine_popup_shown', {
          trigger: 'return_visit',
          path: currentPath,
        });
      }
    } else if (!isPage2) {
      // Mark as visited for future visits
      localStorage.setItem('hasVisitedPatrimoine', 'true');
    }

    return () => clearTimeout(timer);
  }, [currentPath, homePageStartTime, popupTimerDuration]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('patrimoinePopupShown', 'true');
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture('patrimoine_popup_closed', {
        action: 'dismissed',
      });
    }
  };

  const handleDelegate = () => {
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture('patrimoine_popup_delegate_clicked');
    }
    window.location.href = '/home/updatePlan';
    handleClose();
  };

  const resetPopup = () => {
    localStorage.removeItem('patrimoinePopupShown');
    localStorage.removeItem('hasVisitedPatrimoine');
    localStorage.removeItem('todoPopupShown');
    localStorage.removeItem('todoCalendarOpened');
    localStorage.removeItem('cookieConsent');

    Object.keys(localStorage).forEach(key => {
      if (key.includes('isCheck') || key.endsWith('Ok')) {
        localStorage.removeItem(key);
      }
    });

    localStorage.removeItem('diagnosisFormData');
    localStorage.removeItem('diagnosisFormDataIsEnd');
    localStorage.removeItem('leagalFormData');
    localStorage.removeItem('leagalFormDataIsEnd');
    localStorage.removeItem('reglementationFormData');
    localStorage.removeItem('reglementationFormDataIsEnd');
    localStorage.removeItem('curQuestionId');
    localStorage.removeItem('curLeaglFormQuestionId');
    localStorage.removeItem('isAccept');

    setHomePageStartTime(null);
    setIsVisible(false);

    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <>
      <Modal
        open={isVisible}
        footer={null}
        centered
        closable={true}
        onCancel={handleClose}
        wrapClassName="patrimoine-popup"
        width={600}
        style={{ width: '100%', maxWidth: '600px' }}
      >
        <div className="patrimoine-popup__content">
          <h2>
            Si vous ne le faites pas maintenant, vous ne le ferez jamais … et
            vous le savez 😉
          </h2>
          <p>
            Déléguez-nous la mise en place. En 24h, c'est terminé - et cela ne
            vous coûte que <strong>549 €</strong>.
          </p>
          <div className="patrimoine-popup__action">
            <Button
              type="primary"
              onClick={handleDelegate}
              className="patrimoine-popup__button"
              style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            >
              Faites-le pour moi
            </Button>
          </div>
          <button
            className="patrimoine-popup__close"
            onClick={handleClose}
            style={{ width: '100%', maxWidth: '300px', margin: '10px auto' }}
          >
            Plus tard
          </button>
        </div>
      </Modal>

      {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.8)',
            padding: '8px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Button
            type="primary"
            danger
            size="small"
            onClick={resetPopup}
            icon={<span style={{ marginRight: '4px' }}>🔄</span>}
            style={{ fontWeight: 'bold' }}
          >
            Reset All LocalStorage
          </Button>
          <div style={{ fontSize: '10px', color: '#666', textAlign: 'center' }}>
            Dev Tool: Clears all flags &amp; reloads
          </div>
        </div>
      )}
    </>
  );
};

export default PatrimoinePopup;
